import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { mainSiteUrl } from "@/mocks/categories";

const siteUrl = "https://sugargoosheet.casa";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Sugargoo Warehouse Guide: Storage, QC & Processing Explained",
  description: "Everything about Sugargoo warehouse — storage time limits, quality inspection process, package consolidation, and tips for international buyers and dropshippers.",
  image: "https://readdy.ai/api/search-image?query=Modern%20organized%20warehouse%20interior%20with%20shelves%20of%20packaged%20products%20cardboard%20boxes%20neatly%20stacked%20clean%20bright%20industrial%20space%20with%20natural%20light%20coming%20through%20windows%20professional%20logistics%20environment%20minimal%20design&width=1400&height=700&seq=sugargoo-warehouse-guide&orientation=landscape",
  datePublished: "2026-08-09",
  dateModified: "2026-08-09",
  author: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  publisher: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  inLanguage: "en",
  isPartOf: { "@type": "WebSite", name: "Sugargoo Spreadsheet", url: siteUrl },
};

export default function WarehouseGuide() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">Sugargoo Warehouse Guide</span>
            </nav>

            <header className="mb-10">
              <div className="inline-block text-xs font-medium text-accent-500 bg-accent-50 px-3 py-1 rounded-full mb-4">Warehouse</div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                Sugargoo Warehouse Guide: Storage, QC &amp; Processing Explained
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-2xl">
                Everything about Sugargoo warehouse — storage time limits, quality inspection process, package consolidation, and tips for international buyers and dropshippers.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-400">
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i> Updated: August 2026</span>
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i> 6 min read</span>
              </div>
            </header>

            <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img src="https://readdy.ai/api/search-image?query=Modern%20organized%20warehouse%20interior%20with%20shelves%20of%20packaged%20products%20cardboard%20boxes%20neatly%20stacked%20clean%20bright%20industrial%20space%20with%20natural%20light%20coming%20through%20windows%20professional%20logistics%20environment%20minimal%20design&width=1400&height=700&seq=sugargoo-warehouse-guide&orientation=landscape" alt="Sugargoo Warehouse Guide - Storage and Processing" className="w-full h-full object-cover" />
            </div>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">How Sugargoo Warehouse Works</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                When you purchase products through Sugargoo — whether discovered via your <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Sugargoo Spreadsheet</Link> or manual browsing — all items are first shipped from the seller to Sugargoo's warehouse in China. This warehouse serves as a central hub where your items are received, inspected, photographed, stored, and eventually packed for international shipment.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Warehouse Services Overview</h2>
              <div className="overflow-x-auto rounded-xl border border-background-200/70 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-900">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Details</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Free Storage","Up to 100 days from arrival date","Free"],
                      ["Quality Inspection","Basic visual check upon arrival","Free"],
                      ["QC Photos","3 standard photos per item","Free"],
                      ["Detailed QC Photos","Close-up, measurements, tags","From ¥2/photo"],
                      ["Package Consolidation","Combine multiple items into one box","Free"],
                      ["Rehearsal Shipping","Pre-weigh and measure package","¥20/package"],
                      ["Package Reinforcement","Extra bubble wrap, corner protection","¥5-15/package"],
                      ["Brand Tag Removal","Remove branded tags/labels","¥2/item"],
                    ].map((row, i) => (
                      <tr key={i} className={`border-t border-background-200/70 ${i % 2 === 0 ? "bg-background-50" : "bg-background-100"}`}>
                        <td className="px-4 py-3 text-xs font-medium text-foreground-900">{row[0]}</td>
                        <td className="px-4 py-3 text-xs text-foreground-600">{row[1]}</td>
                        <td className="px-4 py-3 text-xs text-primary-600 font-medium">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Quality Inspection Process</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                One of Sugargoo's strongest features is the quality inspection (QC) process. When your item arrives at the warehouse, staff perform a basic visual inspection to check for obvious defects, incorrect sizing, wrong colors, or damage. Standard QC photos are taken — typically a front view, back view, and one detail shot — and uploaded to your account.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                If you spot any issues in the QC photos, you can request a return or exchange with the seller before the item leaves the warehouse. This QC step is crucial for products found through the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Sugargoo Spreadsheet</Link> since it verifies that what was ordered matches what arrived.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Storage Time and Policies</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Sugargoo provides <strong>100 days of free storage</strong> from the date each item arrives at the warehouse. This generous window allows you to accumulate multiple orders before shipping internationally. After 100 days, storage fees may apply, though Sugargoo typically sends reminders well before the deadline. For dropshippers who order frequently, the long storage window means you can batch orders strategically to minimize per-item shipping costs.
              </p>
            </section>

            <div className="bg-background-900 rounded-2xl p-8 md:p-10 text-center mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">Master Sugargoo Warehouse Operations</h2>
              <p className="text-sm text-background-400 max-w-md mx-auto mb-6">Combine warehouse knowledge with the Sugargoo Spreadsheet for seamless product sourcing and fulfillment.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-900 bg-primary-400 hover:bg-primary-300 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer">Get Sugargoo Spreadsheet</a>
                <Link to="/" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-lg transition-all duration-200 whitespace-nowrap">Back to Homepage</Link>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Related Resources</h3>
              <ul className="space-y-1.5">
                <li><Link to="/blog/sugargoo-shipping-cost-guide" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Shipping Cost Guide — Cheapest Methods</Link></li>
                <li><Link to="/blog/sugargoo-review-2026" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Review 2026 — Complete Analysis</Link></li>
                <li><Link to="/" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Spreadsheet Homepage — All Resources</Link></li>
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}